/**
 * App shell. Four tabs — Now, Matches, Terrace, Squad — over the matchday
 * state machine. Explicit queries open interactive details; clean match,
 * player, story and edition paths remain readable reference pages. Desktop is a
 * deliberate two-pane programme spread (content + Terrace rail).
 *
 * Codex navigation: a back-stack of entry IDs renders as an overlay above
 * the tabs. Resolving an ID routes a person to the live squad page and a
 * trophy/season/legend to an EntryPage; the stack lets you wander and return.
 */
import { Suspense, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import { DetailHeader, EmptyNote, Masthead, MobileTabBar, TabBar, type TabId } from './components/chrome';
import { EntryPage } from './components/EntryPage';
import { LoreEntryPage } from './components/Lore';
import { YearView } from './components/YearView';
import { API_BASE } from './lib/api';
import { buildYearView, resolveEntry } from './lib/codex';
import { FEEDS, useFeed } from './lib/feeds';
import { armSafeEdges } from './lib/intents';
import { navReduce, type NavLayer } from './lib/navstack';
import type { CodexFeed, LoreFeed, SquadFeed, TodayDiscussionRoom } from './lib/types';
import { useEdgeSwipe } from './lib/useEdgeSwipe';
import { useModalFocus } from './lib/useModalFocus';
import { armOutbox } from './lib/outbox';
import { useGamePassport } from './lib/useGamePassport';
import { useArchivedGameCatchup } from './lib/game-catchup';
import { recordLandingOnce } from './lib/growth';
import { NowScreen } from './screens/NowScreen';
import { RedThreadHome } from './experience/RedThreadHome';
import { PlayersSection } from './experience/PlayersSection';
import { ThemeControl } from './experience/theme';
import { ProgrammeDisclosure } from './experience/ProgrammeDisclosure';
import { HistorySection } from './experience/HistorySection';
import { MatchdaySection } from './experience/MatchdaySection';
import { phoneLayout, useMobileViewport } from './lib/useMobileViewport';
import { MatchesScreen } from './screens/MatchesScreen';
import { SquadScreen } from './screens/SquadScreen';
import { TerraceScreen } from './screens/TerraceScreen';

const TABS: TabId[] = ['now', 'matches', 'history', 'terrace', 'squad'];
const TAB_KEY = 'coyg_tab';

function initialState(): {
  tab: TabId;
  matchId: string | null;
  playerId: string | null;
  entryId: string | null;
  loreId: string | null;
  yearId: string | null;
  terraceRoom: TodayDiscussionRoom | null;
  terraceThread: string | null;
} {
  const base = { matchId: null, playerId: null, entryId: null, loreId: null, yearId: null, terraceRoom: null, terraceThread: null };
  try {
    const params = new URLSearchParams(window.location.search);
    const segments = window.location.pathname.split('/').filter(Boolean).map(decodeURIComponent);
    const [surface, id] = segments;
    if (surface === 'north-bank') {
      const room = ({
        all: 'all', match: 'match', transfers: 'TRANSFERS', season: 'SEASON', analysis: 'ANALYSIS',
      } as const)[(id ?? 'all').toLowerCase() as 'all' | 'match' | 'transfers' | 'season' | 'analysis'] ?? 'all';
      return { tab: 'terrace', ...base, terraceRoom: room, terraceThread: room === 'match' ? params.get('thread') : null };
    }
    const requestedTab = params.get('tab') as TabId | null;
    const queryTab = requestedTab && TABS.includes(requestedTab) ? requestedTab : 'now';
    const year = params.get('year');
    if (year && /^\d{4}$/.test(year)) return { tab: 'squad', ...base, yearId: year };
    const lore = params.get('lore');
    // A lore deep link lands in the Vault (Club sub-view) with the entry open.
    if (lore) return { tab: 'squad', ...base, loreId: lore };
    const e = params.get('e');
    if (e) return { tab: queryTab, ...base, entryId: e };
    const m = params.get('m');
    const p = params.get('p');
    if (m) return { tab: 'matches', ...base, matchId: m };
    if (p) return { tab: 'squad', ...base, playerId: p };
    if (!requestedTab && phoneLayout()) {
      if (location.hash === '#history') return {tab:'history',...base};
      if (location.hash === '#people') return {tab:'squad',...base};
      if (location.hash === '#matchday') return {tab:'matches',...base};
      if (params.has('player')) return {tab:'squad',...base};
      if (params.has('history')) return {tab:'history',...base};
    }
    return { tab: queryTab, ...base };
  } catch {
    return { tab: 'now', ...base };
  }
}

// The opening nav stack, derived from the deep-link result. At most one layer
// can be seeded (the deep link names a single surface); the order mirrors the
// precedence in initialState (lore → entry → match → player).
function seedLayers(initial: ReturnType<typeof initialState>): NavLayer[] {
  if (initial.yearId) return [{ kind: 'year', id: initial.yearId }];
  if (initial.loreId) return [{ kind: 'lore', id: initial.loreId }];
  if (initial.entryId) return [{ kind: 'entry', id: initial.entryId }];
  if (initial.matchId) return [{ kind: 'match', id: initial.matchId }];
  if (initial.playerId) return [{ kind: 'player', id: initial.playerId }];
  return [];
}

// Can we touch the History API at all? A sandboxed iframe without history
// access throws; compute the capability once so on-screen Back can fall back
// to a pure reducer pop instead of a broken history.back().
const canHistory = (() => {
  try {
    return typeof window !== 'undefined' && !!window.history;
  } catch {
    return false;
  }
})();

let outboxArmed = false;

/** Maps a nav layer to the short title used as the back-button label. */
function layerLabel(
  layer: NavLayer,
  codexData: CodexFeed,
  squadData: SquadFeed,
  loreData: LoreFeed,
): string {
  switch (layer.kind) {
    case 'year':
      return layer.id;
    case 'entry': {
      const r = resolveEntry(layer.id, codexData, squadData);
      return r?.kind === 'codex' ? r.entry.title : 'Entry';
    }
    case 'lore': {
      const e = loreData.entries.find((e) => e.matchId === layer.id);
      return e ? `v ${e.opponentShort}` : 'Match lore';
    }
    case 'match':
      return 'Matches';
    case 'player': {
      const r = resolveEntry(layer.id, codexData, squadData);
      return r?.kind === 'player' ? r.player.name : 'Player';
    }
  }
}

/** Maps a tab id to its short display label for the back button. */
function tabLabel(tab: string): string {
  switch (tab) {
    case 'now': return 'Now';
    case 'matches': return 'Matches';
    case 'history': return 'History';
    case 'terrace': return 'North Bank';
    case 'squad': return 'Squad';
    default: return 'Back';
  }
}

/**
 * A codex/lore entry overlay surfaced above the tabs. It's a modal dialog:
 * labelled, Escape-closable, and it moves focus inside on open (onto the
 * overlay itself, which the screen's "← Back" button anchors) so keyboard and
 * screen-reader users land in the new surface rather than behind it.
 */
const EntryOverlay = ({
  label,
  onClose,
  children,
}: {
  label: string;
  onClose: () => void;
  children: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useModalFocus(ref, onClose);
  return (
    <div
      ref={ref}
      className="entry-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={label}
      tabIndex={-1}
    >
      {children}
    </div>
  );
};

export const App = () => {
  const isPhone = useMobileViewport();
  const initial = useMemo(initialState, []);
  useEffect(() => { recordLandingOnce(); }, []);
  useEffect(() => {
    if (!outboxArmed) {
      outboxArmed = true;
      armOutbox(API_BASE);
    }
    return armSafeEdges();
  }, []);
  const [activeTab, setActiveTab] = useState<TabId>(initial.tab);
  useEffect(() => { document.title = `${{now:'Arsenal now',matches:'Matchbook',history:'Arsenal history',terrace:'North Bank',squad:'Our people'}[activeTab]} — COYG`; }, [activeTab]);
  const [writeFailureNotice, setWriteFailureNotice] = useState<string | null>(null);
  useEffect(() => {
    const onFailure = (event: Event) => {
      const expired = Boolean((event as CustomEvent<{ expired?: boolean }>).detail?.expired);
      setWriteFailureNotice(expired
        ? 'A change saved on this device expired before delivery and was removed. Please submit it again if it still matters.'
        : 'This change could not be saved or queued. Keep this page open and try again.');
      window.setTimeout(() => setWriteFailureNotice(null), 6000);
    };
    window.addEventListener('coyg:write-persistence-failed', onFailure);
    return () => window.removeEventListener('coyg:write-persistence-failed', onFailure);
  }, []);
  // One ordered stack of nav layers (match / player / entry / lore) over the
  // tab. Pushing opens; popping is the universal Back. The legacy single-value
  // fields below are derived from it so screen props and the URL sync survive.
  const [nav, dispatch] = useReducer(navReduce, undefined, () => ({ layers: seedLayers(initial) }));
  const [terraceThread, setTerraceThread] = useState<string | null>(initial.terraceThread);
  // A North Bank room requested from Today's "Discuss" route. Selected on arrival
  // by TerraceScreen; cleared when the user taps a tab so direct nav is neutral.
  const [terraceRoom, setTerraceRoom] = useState<TodayDiscussionRoom | null>(initial.terraceRoom);

  // Detail IDs resolve from the top down. A sibling switch must never leave an
  // older detail winning simply because it entered the stack first.
  const openMatchId = [...nav.layers].reverse().find((l) => l.kind === 'match')?.id ?? null;
  const openPlayerId = [...nav.layers].reverse().find((l) => l.kind === 'player')?.id ?? null;
  const entryStack = nav.layers.filter((l) => l.kind === 'entry').map((l) => l.id);
  const openLoreId = nav.layers.find((l) => l.kind === 'lore')?.id ?? null;
  // A year view is only ever the topmost layer (you open it from a timeline,
  // then pop back). Read it off the top so a deeper layer hides it.
  const top = nav.layers.at(-1);
  const openYearId = top?.kind === 'year' ? top.id : null;

  const match = useFeed(FEEDS.match);
  const fixtures = useFeed(FEEDS.fixtures);
  useGamePassport(fixtures.data.fixtures, fixtures.data.season, fixtures.data.source, fixtures.stale);
  useArchivedGameCatchup(fixtures.data.fixtures, fixtures.online && !fixtures.stale);
  const codex = useFeed(FEEDS.codex);
  const squad = useFeed(FEEDS.squad);
  const lore = useFeed(FEEDS.lore);
  const loreEntry = openLoreId ? lore.data.entries.find((e) => e.matchId === openLoreId) ?? null : null;
  const liveNow = !match.stale && (match.data.phase === 'live' || match.data.phase === 'ht');

  // The label shown on the back button of the topmost overlay — names where
  // you return to rather than a generic "Back" so every drill-down is legible.
  const penultimate = nav.layers.length >= 2 ? nav.layers[nav.layers.length - 2] : null;
  const destinationFocus = useRef(false);
  const mainRef = useRef<HTMLElement>(null);
  const listScroll = useRef<Partial<Record<TabId, number>>>({});
  const detailOriginTab = useRef<TabId | null>(null);
  const pushDestination = useRef(false);
  const resetDestinationHash = useRef(false);
  const archiveSelections = useRef({history:new URLSearchParams(location.search).get('history'),player:new URLSearchParams(location.search).get('player')});
  const currentDestination = useRef(activeTab);
  currentDestination.current = activeTab;
  const currentListDetail = useRef(false);
  currentListDetail.current = (activeTab === 'matches' && Boolean(openMatchId)) || (activeTab === 'squad' && Boolean(openPlayerId));

  const computedBackLabel = penultimate
    ? layerLabel(penultimate, codex.data, squad.data, lore.data)
    : tabLabel(detailOriginTab.current ?? activeTab);

  // The full "where am I" trail: the active tab followed by every ancestor
  // layer below the current surface (we drop the top layer — that *is* the
  // surface you're looking at). DetailHeader only paints it when it's ≥2 deep.
  const crumbTrail = useMemo(
    () => [
      tabLabel(activeTab),
      ...nav.layers
        .slice(0, -1)
        .map((l) => layerLabel(l, codex.data, squad.data, lore.data)),
    ],
    [activeTab, nav.layers, codex.data, squad.data, lore.data],
  );

  const navigate = (tab: TabId) => {
    const selections = new URLSearchParams(location.search);
    if (selections.has('history')) archiveSelections.current.history = selections.get('history');
    if (selections.has('player')) archiveSelections.current.player = selections.get('player');
    setActiveTab(tab);
    try {
      localStorage.setItem(TAB_KEY, tab);
    } catch {
      /* private mode */
    }
  };

  // The single, universal Back. When we have history access we route through
  // history.back() so our pushed entries stay in lockstep with the stack — the
  // popstate handler does the actual reducer pop. Sandboxed (no history): pop
  // directly. Every on-screen close control funnels through here.
  const goBack = () => {
    const stateDepth = Number((window.history.state as { coygDepth?: unknown } | null)?.coygDepth);
    if (canHistory && stateDepth === nav.layers.length) window.history.back();
    else dispatch({ type: 'pop' });
  };

  // Open any codex ID. A person → the live squad page (replace the stack with
  // just that player); otherwise push an entry overlay. Unknown IDs are
  // ignored (the Vault is the safety net).
  const openEntry = (id: string) => {
    const resolved = resolveEntry(id, codex.data, squad.data);
    if (resolved?.kind === 'player') {
      dispatch({ type: 'reset', layers: [{ kind: 'player', id: resolved.player.id }] });
      navigate('squad');
      return;
    }
    if (resolved?.kind === 'codex') dispatch({ type: 'push', layer: { kind: 'entry', id: resolved.entry.id } });
  };
  const closeEntry = goBack;

  // Open a calendar year — any timeline year is a doorway into the year view
  // (the moments from that year across the club's history, folded on read).
  const openYear = (year: string) => dispatch({ type: 'push', layer: { kind: 'year', id: year } });
  const closeYear = goBack;
  const yearView = openYearId ? buildYearView(openYearId, codex.data) : null;

  const topEntryId = entryStack[entryStack.length - 1] ?? null;
  const topEntry = topEntryId ? resolveEntry(topEntryId, codex.data, squad.data) : null;
  const unavailableDetail = top?.kind === 'match' && !fixtures.data.fixtures.some((fixture) => fixture.id === top.id)
    ? 'Match'
    : top?.kind === 'player' && !squad.data.players.some((player) => player.id === top.id)
      ? 'Player'
      : top?.kind === 'entry' && topEntry?.kind !== 'codex'
        ? 'Story'
        : top?.kind === 'lore' && !loreEntry
          ? 'Match story'
          : null;

  // A detail/overlay carries its own contextual Share, so the masthead's
  // app-share hides — one Share per screen, sharing the most specific thing.
  const detailOpen =
    (activeTab === 'matches' && !!openMatchId) ||
    (activeTab === 'squad' && !!openPlayerId) ||
    !!topEntry ||
    !!unavailableDetail ||
    openLoreId !== null ||
    openYearId !== null;
  // Interactive views have explicit app URLs. Clean entity URLs belong to the
  // readable reference pages and remain the URLs used by Share and search.
  const depth = nav.layers.length;
  const prevDepth = useRef(depth);
  const restoringHistory = useRef(false);
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('history')) archiveSelections.current.history = url.searchParams.get('history');
      if (url.searchParams.has('player')) archiveSelections.current.player = url.searchParams.get('player');
      // Phone Home has no embedded archive. Desktop Home still accepts legacy
      // section links, so retain its explicit year/player hints across reloads.
      const embeddedArchives = activeTab === 'now' && !isPhone;
      if (activeTab !== 'history' && !embeddedArchives) url.searchParams.delete('history');
      if (activeTab !== 'squad' && !embeddedArchives) url.searchParams.delete('player');
      if (resetDestinationHash.current) url.hash = '';
      ['m', 'p', 'e', 'lore', 'year', 'tab', 'club', 'thread'].forEach((k) => url.searchParams.delete(k));
      if (activeTab !== 'terrace') url.searchParams.delete('safety');
      let pathname = '/';
      if (openYearId) {
        url.searchParams.set('tab', 'squad');
        url.searchParams.set('year', openYearId);
      } else if (openLoreId) {
        url.searchParams.set('tab', 'squad');
        url.searchParams.set('lore', openLoreId);
      } else if (topEntryId) {
        url.searchParams.set('tab', activeTab);
        url.searchParams.set('e', topEntryId);
      } else if (activeTab === 'matches' && openMatchId) {
        url.searchParams.set('tab', 'matches');
        url.searchParams.set('m', openMatchId);
      } else if (activeTab === 'squad' && openPlayerId) {
        url.searchParams.set('tab', 'squad');
        url.searchParams.set('p', openPlayerId);
      }
      else if (activeTab === 'terrace') {
        pathname = `/north-bank/${String(terraceRoom ?? 'all').toLowerCase()}`;
        if (terraceRoom === 'match' && terraceThread) url.searchParams.set('thread', terraceThread);
        url.searchParams.delete('history');
        url.searchParams.delete('player');
        url.hash = '';
      }
      else if (activeTab === 'matches' || activeTab === 'squad' || activeTab === 'history') url.searchParams.set('tab', activeTab);
      if (activeTab === 'history' && archiveSelections.current.history) url.searchParams.set('history',archiveSelections.current.history);
      if (activeTab === 'squad' && !openPlayerId && archiveSelections.current.player) url.searchParams.set('player',archiveSelections.current.player);
      url.pathname = pathname;
      const priorState = window.history.state as { coygDepth?: number } | null;
      const state = { ...priorState, coygDepth: depth, coygLayers: nav.layers, coygTab: activeTab, coygOriginTab: detailOriginTab.current };
      if (!restoringHistory.current && (depth > prevDepth.current || pushDestination.current)) {
        window.history.pushState(state, '', url.toString());
      } else {
        // A directly opened detail has no in-app Back entry. Preserve that
        // distinction so its Back button returns to the list, not another site.
        window.history.replaceState(priorState?.coygDepth != null ? state : priorState, '', url.toString());
      }
    } catch {
      /* sandboxed iframe without history access */
    }
    prevDepth.current = depth;
    restoringHistory.current = false;
    pushDestination.current = false;
    resetDestinationHash.current = false;
  }, [activeTab, isPhone, openMatchId, openPlayerId, topEntryId, openLoreId, openYearId, terraceRoom, terraceThread, depth, nav.layers]);

  useEffect(() => {
    const onPop = () => {
      if (!currentListDetail.current) listScroll.current[currentDestination.current] = mainRef.current?.scrollTop ?? 0;
      restoringHistory.current = true;
      const restored = initialState();
      const saved = window.history.state as { coygLayers?: NavLayer[]; coygTab?: TabId; coygOriginTab?: TabId } | null;
      const layers = Array.isArray(saved?.coygLayers) && saved.coygLayers.every((layer) =>
        layer != null && ['match', 'player', 'entry', 'lore', 'year'].includes(layer.kind) && typeof layer.id === 'string')
        ? saved.coygLayers : seedLayers(restored);
      const tab = saved?.coygTab && TABS.includes(saved.coygTab) ? saved.coygTab : restored.tab;
      const restoredTop = layers.at(-1);
      if ((tab === 'matches' && restoredTop?.kind === 'match') || (tab === 'squad' && restoredTop?.kind === 'player')) {
        detailOriginTab.current = saved?.coygOriginTab && TABS.includes(saved.coygOriginTab) ? saved.coygOriginTab : tab;
      }
      setActiveTab(tab);
      setTerraceRoom(restored.terraceRoom);
      setTerraceThread(restored.terraceThread);
      dispatch({ type: 'reset', layers });
      try {
        localStorage.setItem(TAB_KEY, tab);
      } catch {
        /* private mode */
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Left-edge swipe-to-back on touch. Routes through goBack (same as on-screen
  // back) so history entries stay in lockstep with the nav stack.
  useEdgeSwipe(goBack, depth > 0);


  const openMatch = (matchId: string) => {
    if (top?.kind !== 'match') {
      listScroll.current[activeTab] = mainRef.current?.scrollTop ?? 0;
      detailOriginTab.current = activeTab;
    }
    destinationFocus.current = true;
    const layer = { kind: 'match', id: matchId } as const;
    // Moving between fixtures is a page turn inside Matchbook, not another
    // drill-down. Keep one Back step and one browser-history entry.
    dispatch(top?.kind === 'match' && activeTab === 'matches'
      ? { type: 'replace-top', layer }
      : { type: 'push', layer });
    navigate('matches');
  };

  const openThread = (matchId: string) => {
    if (isPhone) navigateFromBar('terrace', true);
    setTerraceThread(matchId);
    setTerraceRoom('match');
    navigate('terrace');
  };

  const openPlayer = (playerId: string) => {
    if (top?.kind !== 'player') {
      listScroll.current[activeTab] = mainRef.current?.scrollTop ?? 0;
      detailOriginTab.current = activeTab;
    }
    destinationFocus.current = true;
    dispatch({ type: 'push', layer: { kind: 'player', id: playerId } });
    navigate('squad');
  };

  // Open a match-lore entry as an overlay (the same chrome as a codex entry).
  const openLore = (matchId: string) => dispatch({ type: 'push', layer: { kind: 'lore', id: matchId } });
  const closeLore = goBack;

  const mainScreen = (() => {
    if (unavailableDetail) return (
      <section className="screen" aria-label="Unavailable detail">
        <DetailHeader onBack={goBack} backLabel={computedBackLabel} />
        <h1>{unavailableDetail} unavailable</h1>
        <EmptyNote>This page is not available in the current programme. Return to {tabLabel(activeTab)} to keep browsing.</EmptyNote>
      </section>
    );
    switch (activeTab) {
      case 'matches':
        if (isPhone && !openMatchId) return <MatchdaySection standalone fixtures={fixtures} match={match} onMatch={openMatch} onDiscuss={openThread} onOpenYear={(year) => { archiveSelections.current.history = String(year); navigate('history'); }}/>;
        return (
          <MatchesScreen
            openMatchId={openMatchId}
            onOpenMatch={openMatch}
            onCloseMatch={goBack}
            onOpenThread={openThread}
            onOpenEntry={openEntry}
            onOpenYear={(year) => { archiveSelections.current.history = String(year); navigate('history'); }}
            backLabel={computedBackLabel}
            trail={crumbTrail}
          />
        );
      case 'history':
        return <HistorySection standalone initialYear={archiveSelections.current.history} onOpenEntry={openEntry}/>;
      case 'squad':
        if (!openPlayerId) return <div><PlayersSection standalone initialPlayerId={archiveSelections.current.player}/><ProgrammeDisclosure className="rt-legacy-squad" label="Open the earlier men’s programme profiles"><SquadScreen openPlayerId={null} onOpenPlayer={openPlayer} onClosePlayer={goBack} onOpenTerrace={() => isPhone ? navigateFromBar('terrace', true) : navigate('terrace')} onOpenEntry={openEntry} onOpenLore={openLore} onOpenYear={openYear}/></ProgrammeDisclosure></div>;
        return (
          <SquadScreen
            openPlayerId={openPlayerId}
            onOpenPlayer={openPlayer}
            onClosePlayer={goBack}
            onOpenTerrace={() => isPhone ? navigateFromBar('terrace', true) : navigate('terrace')}
            onOpenEntry={openEntry}
            onOpenLore={openLore}
            onOpenYear={openYear}
            startInClub={initial.loreId !== null}
            backLabel={computedBackLabel}
            trail={crumbTrail}
          />
        );
      case 'terrace':
        return (
          <TerraceScreen
            threadMatchId={terraceThread}
            selectedRoom={terraceRoom}
            onRoomChange={setTerraceRoom}
            onOpenEntry={openEntry}
            onOpenLore={openLore}
          />
        );
      default:
        return (
          <RedThreadHome
            phoneLayout={isPhone}
            initialHistoryYear={archiveSelections.current.history}
            initialPlayerId={archiveSelections.current.player}
            onNavigate={(tab) => navigateFromBar(tab, true)}
            onOpenMatch={openMatch}
            onOpenTerrace={() => isPhone ? navigateFromBar('terrace', true) : navigate('terrace')}
            onOpenPlayer={openPlayer}
            onOpenEntry={openEntry}
            onDiscuss={openThread}
          />
        );
    }
  })();

  const navigateFromBar = (tab: TabId, addHistory = false) => {
    if (tab === activeTab && depth === 0 && (tab !== 'terrace' || (!terraceThread && (!terraceRoom || terraceRoom === 'all')))) {
      mainRef.current?.scrollTo?.({top:0,behavior:window.matchMedia?.('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
      return;
    }
    if (!currentListDetail.current) listScroll.current[activeTab] = mainRef.current?.scrollTop ?? 0;
    pushDestination.current = addHistory && tab !== activeTab;
    resetDestinationHash.current = true;
    destinationFocus.current = true;
    dispatch({ type: 'reset', layers: [] });
    setTerraceRoom(tab === 'terrace' ? 'all' : null);
    setTerraceThread(null);
    navigate(tab);
  };

  const listDetailOpen = (activeTab === 'matches' && Boolean(openMatchId)) || (activeTab === 'squad' && Boolean(openPlayerId));
  const previousListDetailOpen = useRef(listDetailOpen);
  const previousDestination = useRef(activeTab);
  useEffect(() => {
    if (previousDestination.current !== activeTab && !listDetailOpen) {
      const position = listScroll.current[activeTab] ?? 0;
      const frame = window.requestAnimationFrame(() => {if(mainRef.current)mainRef.current.scrollTop=position;});
      previousDestination.current = activeTab;
      return () => window.cancelAnimationFrame(frame);
    }
    previousDestination.current = activeTab;
  },[activeTab,listDetailOpen]);
  useEffect(() => {
    const node = mainRef.current;
    const moveTo = (target: HTMLElement | null, top: number) => {
      if (!target) return;
      if (typeof target.scrollTo === 'function') target.scrollTo({ top });
      else target.scrollTop = top;
    };
    if (listDetailOpen && !previousListDetailOpen.current) moveTo(node, 0);
    if (!listDetailOpen && previousListDetailOpen.current) {
      const origin = detailOriginTab.current;
      const top = origin ? listScroll.current[origin] ?? 0 : 0;
      if (origin === activeTab) window.requestAnimationFrame(() => moveTo(mainRef.current, top));
      detailOriginTab.current = null;
    }
    previousListDetailOpen.current = listDetailOpen;
  }, [activeTab, listDetailOpen]);

  useEffect(() => {
    if (!destinationFocus.current) return;
    destinationFocus.current = false;
    const frame = window.requestAnimationFrame(() => {
      const headings = [...(mainRef.current?.querySelectorAll<HTMLElement>('h1, h2, [role="heading"]') ?? [])];
      const target = headings.find(node=>node.getClientRects().length>0) ?? headings.find(node=>!node.closest('[hidden], details:not([open])') && getComputedStyle(node).display!=='none') ?? mainRef.current;
      target?.setAttribute('tabindex', '-1');
      target?.focus({preventScroll:true});
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeTab, openMatchId, openPlayerId]);

  return (
    <div className={`app-frame red-thread-app${liveNow ? ' app-frame--live' : ''}`}>
      <Masthead live={liveNow} share={!detailOpen} right={<ThemeControl/>} mobileTitle={{now:'Home',matches:'Matchday',history:'History',squad:'Players',terrace:'North Bank'}[activeTab]}/>
      <TabBar active={activeTab} onNav={navigateFromBar} />
      {writeFailureNotice && <div className="app-write-notice" role="alert">{writeFailureNotice}</div>}
      <div className="app-body">
        {/* Keep one scroll container per destination. Detail transitions reset
            to the top, then return supporters to their exact list position. */}
        <main id="main" ref={mainRef} tabIndex={-1} className={`app-main${activeTab === 'terrace' ? ' app-main--terrace' : ''}`} key={activeTab}>
          <Suspense fallback={<div className="screen screen-loading" role="status">Opening the programme…</div>}>{mainScreen}</Suspense>
          {activeTab !== 'terrace' && <footer className="site-disclaimer">
            Unofficial supporter project. Not affiliated with or endorsed by Arsenal Football Club.
          </footer>}
        </main>
      </div>
      <MobileTabBar active={activeTab} onNav={tab=>navigateFromBar(tab,true)}/>
      {!unavailableDetail && topEntry?.kind === 'codex' && !loreEntry && !yearView && (
        <EntryOverlay key={topEntry.entry.id} label={topEntry.entry.title} onClose={closeEntry}>
          <EntryPage entry={topEntry.entry} onOpen={openEntry} onBack={closeEntry} onOpenYear={openYear} backLabel={computedBackLabel} trail={crumbTrail} />
        </EntryOverlay>
      )}
      {!unavailableDetail && loreEntry && !yearView && (
        <EntryOverlay
          key={loreEntry.matchId}
          label={`Arsenal ${loreEntry.result.score.home}–${loreEntry.result.score.away} ${loreEntry.opponent}`}
          onClose={closeLore}
        >
          <LoreEntryPage entry={loreEntry} onBack={closeLore} backLabel={computedBackLabel} trail={crumbTrail} />
        </EntryOverlay>
      )}
      {yearView && (
        <EntryOverlay key={`year:${yearView.year}`} label={yearView.year} onClose={closeYear}>
          <YearView view={yearView} onOpen={openEntry} onBack={closeYear} backLabel={computedBackLabel} trail={crumbTrail} />
        </EntryOverlay>
      )}
    </div>
  );
};
